import { ButtonColor } from "@/common/enum/button-color";
import { DepartmentDto } from "@/types/dto/department-dto";
import { Button, Modal, TextInput } from "flowbite-react";
import React, { useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { toastState } from "../../atoms/system/toast-state";
import { departmentPageState } from "../../atoms/user/department-page-state";
import { departmentService } from "../../services/department-service";
import { UpdateDepartmentPayload } from "../../types/payload/update-department-payload";
import { CustomHelperText } from "../CustomHelperText";
import { TextInputLabel } from "../label/TextInputLabel";
import { H4 } from "../typography/H4";

interface Props {
  selectedDepartment?: DepartmentDto;
  closeModal: () => void;
}

export const PlatformDepartmentModal: React.FC<Props> = ({
  selectedDepartment,
  closeModal,
}) => {
  const [payload, setPayload] = useState<UpdateDepartmentPayload>({
    name: selectedDepartment?.name || "",
  });

  const [aggregateErrors, setAggregateErrors] = useState({
    name: "",
  });

  const [page, setPage] = useRecoilState(departmentPageState);
  const setToast = useSetRecoilState(toastState);

  const onChangePayload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload({ ...payload, [name]: value });
  };

  const handleCreateClick = async () => {
    const createResponse = await departmentService.create(payload);
    setAggregateErrors({
      name: createResponse.data.aggregateErrors?.name || "",
    });
    if (createResponse.ok) {
      const pageResponse = await departmentService.getPage({
        page: page.currentPage,
        size: 100,
      });
      if (!pageResponse.ok) {
        return;
      }
      setPage(pageResponse.data);
      setToast({
        show: true,
        messages: ["부서를 추가했습니다."],
      });
      closeModal();
    }
  };

  const handleUpdateClick = async () => {
    const updateByIdresponse = await departmentService.updateById(
      selectedDepartment!.id,
      payload
    );
    setAggregateErrors({
      name: updateByIdresponse.data.aggregateErrors?.name || "",
    });
    if (updateByIdresponse.ok) {
      const getByIdResponse = await departmentService.getById(
        selectedDepartment!.id
      );
      if (!getByIdResponse.ok) {
        return;
      }
      setPage((prev) => ({
        ...prev,
        contents: prev.contents.map((item) =>
          item.id === selectedDepartment!.id ? getByIdResponse.data : item
        ),
      }));
      setToast({
        show: true,
        messages: ["부서를 수정했습니다."],
      });
      closeModal();
    }
  };

  const handleDeleteClick = async () => {
    const response = await departmentService.deleteById(selectedDepartment!.id);
    if (!response.ok) {
      return;
    }
    setPage((prev) => ({
      ...prev,
      contents: prev.contents.filter(
        (item) => item.id !== selectedDepartment!.id
      ),
    }));
    setToast({
      show: true,
      messages: ["부서를 삭제했습니다."],
    });
    closeModal();
  };

  const disabled = !payload.name || selectedDepartment?.name === payload.name;

  const isNotValidName = Boolean(aggregateErrors.name);

  return (
    <Modal size="2xl" show={true} onClose={closeModal}>
      <Modal.Header className="p-[24px] h-[76px]">
        <H4>{selectedDepartment ? "부서 수정" : "부서 추가"}</H4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex justify-center">
          <div className="flex flex-col gap-[8px]">
            <TextInputLabel required>부서명</TextInputLabel>
            <TextInput
              required
              className="w-[572px]"
              placeholder="상호명을 입력해주세요"
              name="name"
              value={payload.name}
              onChange={onChangePayload}
            />
            {isNotValidName && (
              <CustomHelperText
                isError={isNotValidName}
                text={aggregateErrors.name}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-[16px]">
          {selectedDepartment && (
            <Button color={ButtonColor.RED} onClick={handleDeleteClick}>
              부서 삭제하기
            </Button>
          )}
          <Button
            disabled={disabled}
            color={disabled ? ButtonColor.DISABLED : ButtonColor.PRIMARY}
            onClick={selectedDepartment ? handleUpdateClick : handleCreateClick}
          >
            {selectedDepartment ? "수정하기" : "추가하기"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
